import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { App as AntdApp, ConfigProvider, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { ANTD_LOCALES } from '@/app/i18n/antdLocales'
import { currentLanguage, isRtl } from '@/app/i18n'
import { ThemeContext, type ThemeContextValue, type ThemeMode } from './ThemeContext'
import { darkTheme, lightTheme } from './tokens'

const STORAGE_KEY = 'xsib-selfcare.theme'

const BROWSER_BAR_COLOR: Record<ThemeMode, string> = {
  light: theme.getDesignToken(lightTheme).colorBgContainer,
  dark: theme.getDesignToken(darkTheme).colorBgContainer,
}

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // localStorage mavjud bo'lmasligi mumkin
  }
  // Saqlangan tanlov yo'q — standart rejim QORONG'I (egasi qarori,
  // 2026-08-08).
  //
  // ⚠️ Ilgari bu yerda 'light' turardi va sabab yozilgan edi: "abonent
  // telefonidan kunduzi kiradi". Qaror o'zgardi — uch ilova ham bir xil
  // ko'rinishga keltirildi, chunki bitta mahsulotning ikki qismi ikki
  // xil ochilishi tayyor emasdek taassurot berardi.
  //
  // Bu FAQAT standart: tanlash tugmasi joyida qoladi va tanlov
  // `localStorage` da saqlanadi.
  return 'dark'
}

/**
 * antd `ConfigProvider` — tema (algoritm + tokenlar) va antd komponentlarining
 * O'Z tili (`locale`) shu bitta joyda birga turadi — aks holda til
 * o'zgarganda jadval/pagination matnlari eskisida qolib ketardi.
 */
export function AppThemeProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // e'tiborsiz
    }
    document.documentElement.style.colorScheme = mode
    document.documentElement.dataset['theme'] = mode
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BROWSER_BAR_COLOR[mode])
  }, [mode])

  const setMode = useCallback((next: ThemeMode) => setModeState(next), [])
  const toggleMode = useCallback(
    () => setModeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  )

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, isDark: mode === 'dark', setMode, toggleMode }),
    [mode, setMode, toggleMode],
  )

  const antdLocale = ANTD_LOCALES[currentLanguage()]
  void i18n.resolvedLanguage // til o'zgarishiga bog'liqlikni aniq ko'rsatadi

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={mode === 'dark' ? darkTheme : lightTheme} locale={antdLocale} direction={isRtl() ? 'rtl' : 'ltr'}>
        {/* antd `App` — `message`/`modal` ni hook orqali ishlatish uchun.
            Statik chaqiruv tema tokenlarini OLMAYDI va qorong'i rejimda
            oq quti bo'lib chiqadi. */}
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}
